import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import personIcon from 'assets/images/icons/person.png';
import peopleIcon from 'assets/images/icons/people.png';
import { ReactComponent as SingleGoalAdd } from 'assets/images/icons/single-goal-add.svg';
import { ReactComponent as TeamGoalAdd } from 'assets/images/icons/team-goal-add.svg';
import toolIcon from 'assets/images/icons/tool.png';

import { ReactComponent as ScaleIcon } from 'assets/images/icons/scale.svg';
import { Button, Input, InputAffix } from "components/ui";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { useForm } from "react-hook-form";
import { empty, errorMessage, formatNumberTo, navHeight } from "helpers/common";
import { useTranslation } from "translation/useTranslation";
import { getLang } from "store/userSlice";
import EditorInput from "components/ui/EditorInput";
import ExpandableHtml from "components/ui/ExpandableHtml";
import Link from "components/ui/Link";
import ErrorAlert from "components/ui/ErrorAlert";
import { Checkbox } from "components/ui";

function TeamObjective({ data, mode = 'add', settings, setError, activeObjective, setActiveObjective, setActiveSystematicObjective, balancedScorecardDetails, fetchBalancedScorecardDetails, setDisplayTeamObjectiveSelect, readOnlyMode, totalIndividual, setActiveObjectiveDirty, weighted }) {

  const { translate, editableTranslate } = useTranslation();

  const [weight, setWeight] = useState(data?.weight || null);
  const [weightError, setWeightError] = useState(false);

  const handleClick = (e) => {
    if (e.target.getAttribute('data-key') === 'button.cancel' || (activeObjective === data.hash_id && e.target.tagName === 'DIV')) {
      setActiveObjective(null);
    } else {
      setActiveSystematicObjective(null);
      setActiveObjective(data.hash_id);
    }
  };

  const handleSelectChange = (e) => {
    if (e.target.checked) {
      setActiveSystematicObjective(null);
      setActiveObjective(data.hash_id);
    } else {
      setActiveObjective(null);
    }
  };

  const renderSelectCheckbox = () => mode === 'add' && <span data-testid={`team-objective-select-${data.hash_id}`} className="d-inline-flex mr-2" onClick={(e) => e.stopPropagation()}>
    <Checkbox data-testid="select-checkbox"
      id={`select-team-objective-${data.hash_id}`}
      checked={activeObjective === data.hash_id}
      onChange={handleSelectChange}
      disabled={readOnlyMode}
      aria-label={data.name}
      className="mb-0"
    />
  </span>;

  const handleWeightChange = (e) => {
    setActiveObjectiveDirty(true);
    setWeightError(false);
    setWeight(e.target.value);
  };

  const renderEvaluationLevelCol = (type, settings) => {
    if (!settings) return;

    let keyName = 'general';
    let arr = settings.generalPerformanceLevelGrid;

    if (type === 'individual' && settings?.individualObjectivePerformanceLevelGrid && settings?.individualObjectivePerformanceLevelGrid[0]?.individualObjectiveLevel) {
      keyName = 'individualObjective';
      arr = settings.individualObjectivePerformanceLevelGrid;
    }

    return (
      <table className="mb-0">
        <thead>
          <tr>
            <th className="text-center">{editableTranslate('balance_scorecard.level')}</th>
            <th className="text-center">{editableTranslate('balance_scorecard.description')}</th>
            <th className="text-center">{editableTranslate('balanced_scorecard.definition')}</th>
          </tr>
        </thead>
        <tbody>
          {
            arr?.map((item, i) => <tr key={i}>
              <td className="text-right pr-2">{item[keyName + 'Level']}</td>
              <td className="pl-2">{item[keyName + 'Description']}</td>
              <td className="pl-2">{data?.levels?.filter(l => l.level == item[keyName + 'Level'])[0]?.definition || ''}</td>
            </tr>)
          }
        </tbody>
      </table>
    );
  };

  const handleSave = async () => {
    setError(null);

    if (weighted && !empty(weight)) {
      const temp = Number(formatNumberTo(weight, '.'));
      if (isNaN(temp) || temp < 0 || temp > 100) {
        setWeightError(true);
        setError(translate('form.valid_range') + ' 0-100');
        return;
      }
    }

    const formData = {
      team_objective_hash_id: data.hash_id,
      weight
    };

    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/team-objectives`, formData);
      if (status === 200) {
        await fetchBalancedScorecardDetails(false);
        setDisplayTeamObjectiveSelect(false);
        setActiveObjective(null);
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save team objective'));
      Sentry.captureException(err);
    }
  };

  const onDelete = async (hash_id) => {
    setError(null);

    try {
      const { status } = await api.delete(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/team-objectives/${hash_id}`);
      if (status === 200) {
        await fetchBalancedScorecardDetails(false);
        setActiveObjective(null);
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'delete team objective'));
      Sentry.captureException(err);
    }
  };

  return <>
    {
      activeObjective !== data.hash_id && <div data-testid={`team-objective-${data.hash_id}`} className={`objective-title-div team`} onClick={handleClick}>
        <span className="font-weight-bold title-span">{renderSelectCheckbox()}<img src={peopleIcon} alt="People icon" /> {data.name}</span>
        {
          mode === 'edit' && <div className="position-relative">
            {
              totalIndividual > 100 && <>
                <i className="bi bi-exclamation-circle-fill objective-warning-icon" data-tooltip-id={`individiual-objective-warning-${data.hash_id}`}></i>
                <ReactTooltip id={`individiual-objective-warning-${data.hash_id}`}>{translate('tooltips.evaluation.validation.total_weight_individual')}</ReactTooltip>
              </>
            }
            {
              weighted && <div className="scale-div"><ScaleIcon /> {data.weight}%</div>
            }
          </div>
        }
      </div>
    }
    {
      activeObjective === data.hash_id && <div className={`objective-edit-div team`}>
        <div data-testid={`team-objective-header-${data.hash_id}`} className="d-flex justify-content-between header-div" onClick={handleClick}>
          <span className="font-weight-bold title-span">{renderSelectCheckbox()}<img src={peopleIcon} alt="People icon" /> {data.name} <i className="bi bi-info-circle v-middle ml-1" id="info-select-team-objective-description"></i></span>
          <div className="d-flex align-items-center">
            {
              !readOnlyMode && <>
                {
                  mode === 'edit' ? <>
                    <Button variant="ghost" size="inline" icon="bi-trash" id={`delete-team-objective-${data.hash_id}`} data-testid="delete-button">{editableTranslate('global.delete')}</Button> | 
                  </> : <>
                    <Button variant="ghost" size="inline" data-key="button.cancel" data-testid="cancel-button">{editableTranslate('button.cancel')}</Button> | 
                  </>
                }
                <Button onClick={handleSave} variant="ghost" className="mr-2" size="inline" icon="bi-floppy" data-testid="save-button">{editableTranslate('button.save')}</Button>
              </>
            }
            {
              weighted && <div className="scale-div">
                <ScaleIcon />
                <Input data-testid="weight-input" type="text" size="sm" className="text-right" error={weightError} value={weight || ''} onChange={handleWeightChange} readOnly={readOnlyMode} maxLength={6} />%
              </div>
            }
          </div>
        </div>

        <ReactTooltip anchorSelect={`#delete-team-objective-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
          <Button onClick={() => onDelete(data.hash_id)} variant="primary" size="sm" className="font-weight-normal" data-testid="confirm-button">{editableTranslate('form.delete.yes')}</Button>
        </ReactTooltip>

        <div className="bg-white p-3 form-div">
          <div className="row">
            <div className="col-6">
              <span className="font-weight-bold">{editableTranslate('balanced_scorecard.objective_description')}</span>
              <ExpandableHtml html={data?.description || '-'} />
            </div>
            <div className="col-6">
              <span className="font-weight-bold">{editableTranslate('balanced_scorecard.details_milestones')}</span>
              <ExpandableHtml html={data?.details || '-'} />
            </div>
          </div>

          <div className="levels-div px-3 mt-2">
            <div className="row">
              {renderEvaluationLevelCol('individual', settings)}
            </div>
          </div>
        </div>
      </div>
    }
  </>
}

function IndividualObjective({ data, mode, settings, setError, setDisplay, activeObjective, setActiveObjective, setActiveSystematicObjective, balancedScorecardDetails, fetchBalancedScorecardDetails, readOnlyMode, totalIndividual, setActiveObjectiveDirty, weighted }) {
  const editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', { 'list': 'bullet' }, { 'list': 'ordered' }]
    ]
  };

  const editorFormats = ['bold', 'italic', 'underline', 'bullet', 'list'];

  const lang = useSelector(getLang);
  const { translate, editableTranslate } = useTranslation();

  const [levelKey, setLevelKey] = useState('');
  const [levelGrid, setLevelGrid] = useState([]);

  const [descriptionError, setDescriptionError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  const schema = yup.object({
    name: yup.string().required().max(255),
    description: yup.string().notRequired(),
    details: yup.string().notRequired(),
    levels: yup.array().of(yup.object().shape({
      level: yup.string().required(),
      definition: yup.string().required().max(255)
    })).required(),
    weight: weighted ? yup.string()
      .transform(cv => cv ? cv : '')
      .test('number-range', translate('form.valid_range') + ' 0-100', value => {
        const temp = Number(formatNumberTo(value, '.'));
        return temp >= 0 && temp <= 100;
      }).required() :
      yup.string().notRequired()
  });

  const { register, reset, watch, getValues, setValue, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchDescription = watch('description');
  const watchDetails = watch('details');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (mode === 'add' || activeObjective === data.hash_id) setActiveObjectiveDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    let keyName = 'general';
    let arr = settings.generalPerformanceLevelGrid;

    if (settings?.individualObjectivePerformanceLevelGrid && settings?.individualObjectivePerformanceLevelGrid[0]?.individualObjectiveLevel) {
      keyName = 'individualObjective';
      arr = settings.individualObjectivePerformanceLevelGrid;
    }

    setLevelKey(keyName);
    setLevelGrid(arr);

    const levels = [];
    arr.forEach(l => {
      const level = l[keyName + 'Level'];
      const curLevel = data.levels ? data.levels.filter(al => al.level == level) : [];

      levels.push({
        level,
        definition: (curLevel.length > 0 ? curLevel[0].definition : '')
      });
    })

    reset({
      name: data?.name,
      description: data?.description,
      details: data?.details,
      levels,
      weight: data?.weight
    });
  }, []);

  useEffect(() => {
    setDescriptionError(false);
    setDetailsError(false);
  }, [activeObjective]);

  const renderEvaluationLevelCol = () => {
    return (
      <table className="mb-0">
        <thead>
          <tr>
            <th className="text-center">{editableTranslate('balance_scorecard.level')}</th>
            <th className="text-center">{editableTranslate('balance_scorecard.description')}</th>
            <th className="text-center">{editableTranslate('balanced_scorecard.definition')}</th>
          </tr>
        </thead>
        <tbody>
          {
            levelGrid?.map((item, i) => <tr key={i}>
              <td className="text-right pr-2">{item[levelKey + 'Level']}</td>
              <td className="pl-2">{item[levelKey + 'Description']}</td>
              <td><Input type="text" size="sm" error={!!errors?.levels?.[i]?.definition} {...register(`levels.${i}.definition`)} readOnly={readOnlyMode} maxLength={255} /></td>
            </tr>)
          }
        </tbody>
      </table>
    );
  };

  const handleClick = (e) => {
    if (activeObjective === data.hash_id && e.target.tagName === 'DIV') {
      setActiveObjective(null);
    } else {
      setActiveSystematicObjective(null);
      setActiveObjective(data.hash_id);
    }
  };

  const onSubmit = async (formData) => {
    if (descriptionError || detailsError) return;

    setError(null);

    if (!balancedScorecardDetails) return;

    if (mode === 'edit') formData.hash_id = data.hash_id;

    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/individual-objectives`, formData);
      if (status === 200) {
        if (mode === 'add') setDisplay(false);
        setActiveObjective(null);
        await fetchBalancedScorecardDetails(false);

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save individual objective'));
      Sentry.captureException(err);
    }
  };

  const onDelete = async (hash_id) => {
    setError(null);

    try {
      const { status } = await api.delete(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/individual-objectives/${hash_id}`);
      if (status === 200) {
        setActiveObjective(null);
        await fetchBalancedScorecardDetails(false);

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'delete individual objective'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      {
        (mode !== 'add' && activeObjective !== data.hash_id) && <div data-testid={`individual-objective-${data.hash_id}`} className={`objective-title-div individual`} onClick={handleClick}>
          <span className="font-weight-bold title-span"><img src={personIcon} alt="Person icon" /> {data.name}</span>
          <div className="position-relative">
            {
              totalIndividual > 100 && <>
                <i className="bi bi-exclamation-circle-fill objective-warning-icon" data-tooltip-id={`objective-warning-${data.hash_id}`}></i>
                <ReactTooltip id={`objective-warning-${data.hash_id}`}>{translate('tooltips.evaluation.validation.total_weight_individual')}</ReactTooltip>
              </>
            }
            {
              weighted && <div className="scale-div"><ScaleIcon /> {data.weight}%</div>
            }
          </div>
        </div>
      }
      {
        (mode === 'add' || activeObjective === data.hash_id) && <form className={`objective-edit-div individual`} onSubmit={handleSubmit(onSubmit)}>
          <div data-testid={`individual-objective-header-${data.hash_id || 'add'}`} className="d-flex justify-content-between header-div" onClick={handleClick}>
            <span className="font-weight-bold title-span w-60-percent"><img src={personIcon} alt="Person icon" /> <Input type="text" size="sm" error={!!errors?.name} {...register('name')} readOnly={readOnlyMode} maxLength={255} data-testid="name-input" /></span>
            <div className="d-flex align-items-center">
              {
                !readOnlyMode && <>
                  {
                    mode === 'add' ? <>
                      <Button variant="ghost" size="inline" onClick={() => setDisplay(false)} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button> |
                    </> : <>
                      <Button variant="ghost" size="inline" icon="bi-trash" id={`delete-individual-objective-${data.hash_id}`} data-testid="delete-button">{editableTranslate('global.delete')}</Button> |
                    </>
                  }
                  <Button type="submit" variant="ghost" className="mr-2" size="inline" icon="bi-floppy" data-testid="save-button">{editableTranslate('button.save')}</Button>
                </>
              }
              {
                weighted && <div className="scale-div">
                  <ScaleIcon />
                  <Input type="text" size="sm" className="text-right" {...register('weight')} readOnly={readOnlyMode} maxLength={6} data-testid="weight-input" />%
                </div>
              }
            </div>
          </div>

          <ReactTooltip anchorSelect={`#delete-individual-objective-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
            <Button onClick={() => onDelete(data.hash_id)} variant="primary" size="sm" className="font-weight-normal" data-testid="confirm-button">{editableTranslate('form.delete.yes')}</Button>
          </ReactTooltip>

          <div className="bg-white p-3 form-div">
            <div className="row">
              <div className="col-6">
                <span className="font-weight-bold">{editableTranslate('balanced_scorecard.objective_description')}</span>
                <EditorInput data-testid="description-input"
                  className={`mt-2 ${readOnlyMode ? 'hide-toolbar' : ''}`}
                  value={watchDescription}
                  onChangeCallback={(v) => setValue('description', v, { shouldDirty: true, shouldValidate: true })}
                  validate={(v) => v ? setDescriptionError(true) : setDescriptionError(false)}
                  readOnly={readOnlyMode} />
              </div>
              <div className="col-6">
                <span className="font-weight-bold">{editableTranslate('balanced_scorecard.details_milestones')}</span>
                <EditorInput data-testid="details-input"
                  className={`mt-2 ${readOnlyMode ? 'hide-toolbar' : ''}`}
                  value={watchDetails}
                  onChangeCallback={(v) => setValue('details', v, { shouldDirty: true, shouldValidate: true })}
                  validate={(v) => v ? setDetailsError(true) : setDetailsError(false)}
                  readOnly={readOnlyMode} />
              </div>
            </div>

            <div className="levels-div px-3 mt-4">
              <div className="row">
                {renderEvaluationLevelCol('individual')}
              </div>
            </div>
          </div>
        </form>
      }
    </>
  );
}

function SystematicObjective({ index, data, settings, balancedScorecardDetails, hasSub, activeSystematicObjective, setActiveSystematicObjective, setActiveObjective, saveAgreement, readOnlyMode, setActiveSystematicObjectiveDirty, setError, weighted }) {
  const { translate, editableTranslate } = useTranslation();
  const lang = useSelector(getLang);

  const [detailsError, setDetailsError] = useState(false);

  const savedSystematicObjective = useMemo(() => {
    if (balancedScorecardDetails?.agreement_data?.systematicObjectives && Array.isArray(balancedScorecardDetails?.agreement_data?.systematicObjectives)) {
      for (const obj of balancedScorecardDetails?.agreement_data.systematicObjectives) {
        if (obj.name === data.systematicObjectiveGridName) return obj;
      }
    }
    return null;
  }, [balancedScorecardDetails]);

  const schema = yup.object({
    name: yup.string().required(),
    agreed: yup.boolean().notRequired(),
    details: yup.string().notRequired(),
    weight: weighted ? yup.string()
      .transform(cv => cv ? cv : '')
      .test('number-range', translate('form.valid_range') + ' 0-100', value => {
        const temp = Number(formatNumberTo(value, '.'));
        return temp >= 0 && temp <= 100;
      }).required() :
      yup.string().notRequired(),
    sub: yup.array().of(yup.object().shape({
      name: yup.string().required(),
      agreed: yup.boolean().notRequired(),
      weight: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-100', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 100;
        })
    })).notRequired()
  });

  const { register, reset, watch, getValues, setValue, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchAgreed = watch('agreed');
  const watchSub = watch('sub');
  const watchDetails = watch('details');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (activeSystematicObjective === index) setActiveSystematicObjectiveDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    const defaultValues = {
      name: data.systematicObjectiveGridName,
      details: savedSystematicObjective?.details,
      weight: (savedSystematicObjective?.weight || data.systematicObjectiveGridWeighted)
    };

    if (savedSystematicObjective?.hasOwnProperty('agreed')) {
      defaultValues.agreed = savedSystematicObjective.agreed;
    } else {
      defaultValues.agreed = data.systematicObjectiveAgreeObjective;
    }

    if (hasSub) {
      if (savedSystematicObjective?.sub) defaultValues.sub = savedSystematicObjective?.sub;
      else {
        defaultValues.sub = [];
      }

      for (let i = 0; i < data.systematicObjectivesubCriteria.length; i++) {
        if (!defaultValues.sub[i]) defaultValues.sub[i] = {};

        defaultValues.sub[i].name = data.systematicObjectivesubCriteria[i].systematicSubObjectiveGridName;
        if (savedSystematicObjective?.sub?.[i] && savedSystematicObjective?.sub?.[i].hasOwnProperty('agreed')) {
          defaultValues.sub[i].agreed = savedSystematicObjective?.sub?.[i]?.agreed;
        } else {
          defaultValues.sub[i].agreed = data.systematicObjectivesubCriteria[i].systematicSubObjectiveAgreeObjective;
        }
        defaultValues.sub[i].weight = (savedSystematicObjective?.sub?.[i]?.weight || data.systematicObjectivesubCriteria[i].systematicSubObjectiveGridWeighted);
      }
    }

    reset(defaultValues);
  }, [savedSystematicObjective]);

  useEffect(() => {
    setDetailsError(false);
  }, [activeSystematicObjective]);

  const handleClick = (e) => {
    if (activeSystematicObjective === index && e.target.tagName === 'DIV') {
      setActiveSystematicObjective(null);
    } else {
      setActiveObjective(null);
      setActiveSystematicObjective(index);
    }
  };

  const onSubmit = async () => {
    if (!balancedScorecardDetails || readOnlyMode || detailsError) return;

    let agreementData = balancedScorecardDetails?.agreement_data;
    if (!agreementData) agreementData = {};

    if (!agreementData.systematicObjectives) agreementData.systematicObjectives = [];

    let found = false;
    for (let i = 0; i < agreementData.systematicObjectives.length; i++) {
      if (agreementData.systematicObjectives[i].name === data.systematicObjectiveGridName) {
        agreementData.systematicObjectives[i].agreed = getValues('agreed');
        agreementData.systematicObjectives[i].details = getValues('details');
        agreementData.systematicObjectives[i].weight = getValues('weight');

        if (hasSub) {
          const subs = getValues('sub');
          if (!agreementData.systematicObjectives[i].sub) agreementData.systematicObjectives[i].sub = subs;
          else {
            for (const sub of subs) {
              let subFound = false;
              for (let j = 0; j < agreementData.systematicObjectives[i].sub.length; j++) {
                if (agreementData.systematicObjectives[i].sub[j].name === sub.name) {
                  agreementData.systematicObjectives[i].sub[j].agreed = sub.agreed;
                  agreementData.systematicObjectives[i].sub[j].weight = sub.weight;

                  subFound = true;
                  break;
                }
              }
              if (!subFound) agreementData.systematicObjectives[i].sub.push(sub);
            }
          }

        }

        found = true;
        break;
      }
    }
    if (!found) {
      agreementData.systematicObjectives.push(getValues());
    }

    // validate
    for (const obj of agreementData.systematicObjectives) {
      if (obj.agreed && hasSub) {
        let countSubAgreed = 0;
        for (const sub of obj.sub) {
          if (sub.agreed) countSubAgreed++;
        }
        if (countSubAgreed < 1) {
          setError({ translationKey: 'evaluation.validation.at_least_one_agreed_sub_criteria' });
          return;
        }
      }
    }

    setError(null);

    await saveAgreement(agreementData);
    setActiveSystematicObjective(null);
  };

  const isFixedWeigt = (obj, isSub = false) => {
    if (obj?.weighted) {
      if (['manual', 'auto'].includes(obj?.weighted?.mode)) return true;
    } else {
      if (!isSub && obj?.systematicObjectiveGridWeighted) return true;
      else if (isSub && obj?.systematicSubObjectiveGridWeighted) return true;
    }
    return false;
  };

  return <div className="mt-3">
    {
      activeSystematicObjective !== index && <div data-testid={`systematic-objective-${data.systematicObjectiveGridName}`} className={`objective-title-div systematic mt-3`} onClick={handleClick}>
        <span className="font-weight-bold title-span"><img src={toolIcon} alt="Tool icon" /> {data.systematicObjectiveGridName}</span>
        {
          weighted && <div className={`scale-div ${data?.systematicObjectiveAgreeObjective || savedSystematicObjective?.agreed || watchAgreed ? '' : 'invisible'}`}><ScaleIcon /> {savedSystematicObjective?.weight || data.systematicObjectiveGridWeighted}%</div>
        }
      </div>
    }
    {
      activeSystematicObjective === index && <form className="objective-edit-div systematic" onSubmit={handleSubmit(onSubmit)}>
        <div data-testid={`systematic-objective-header-${data.systematicObjectiveGridName}`} className="d-flex justify-content-between header-div" onClick={handleClick}>
          <span className="font-weight-bold title-span"><img src={toolIcon} alt="Tool icon" /> {data.systematicObjectiveGridName}</span>
          <div className="d-flex align-items-center">
            <label className="mb-0 mr-4">
              <input type="checkbox" className="form-check-input" {...register('agreed')} disabled={data?.systematicObjectiveAgreeObjective || readOnlyMode} data-testid="agreed-checkbox" />
              <span className="font-weight-bold ml-2 v-middle">{editableTranslate('balanced_scorecard.agreed')}</span>
            </label>
            {
              !readOnlyMode && <Button type="submit" variant="ghost" className="mr-2" size="inline" icon="bi-floppy" data-testid="save-button">{editableTranslate('button.save')}</Button>
            }
            {
              watchAgreed && weighted && <div className="scale-div">
                <ScaleIcon />
                <Input type="text" size="sm" className="text-right" {...register('weight')} readOnly={isFixedWeigt(data) || readOnlyMode} maxLength={6} data-testid="weight-input" />%
              </div>
            }
          </div>
        </div>
        <div className={`agreement-systematic-div bg-white ${hasSub ? 'has-sub' : ''}`}>
          <table className="table table-borderless" data-testid="systematic-objectivesub-criteria-table">
            <thead>
              <tr>
                <th colSpan={hasSub ? (weighted ? 2 : 3) : 3}></th>
                <th className={`text-center ${hasSub ? '' : 'w-1percent'}`}></th>
                {
                  (hasSub && weighted) && <th className="text-right">{editableTranslate('balance_scorecard.systematic_objective.weighted')}</th>
                }
              </tr>
            </thead>
            <tbody>
              {
                (hasSub || data.systematicObjectiveGridDescription) && <tr>
                  <td colSpan={hasSub ? 2 : 3} className={hasSub ? '' : 'no-sub desc'}>{hasSub ? data.systematicObjectiveGridName : data.systematicObjectiveGridDescription}</td>
                  <td className={`text-center ${hasSub ? '' : 'w-1percent'}`}></td>
                  {
                    hasSub && <td></td>
                  }
                </tr>
              }
              {
                hasSub && data.systematicObjectivesubCriteria.map((sub, j) => <tr key={j}>
                  <td className={`arrow-td ${j < data.systematicObjectivesubCriteria.length - 1 ? 'has-next' : ''} text-right position-relative`}><i className="bi bi-chevron-right"></i></td>
                  <td colSpan={weighted ? 1 : 2}>{sub.systematicSubObjectiveGridName}</td>
                  <td className="text-center">
                    <input type="checkbox" className="form-check-input" {...register(`sub.${j}.agreed`)} disabled={sub?.systematicSubObjectiveAgreeObjective || readOnlyMode} data-testid={`systematic-objective-${index}-sub-${j}-agreed`} />
                  </td>
                  {
                    weighted && <td className="pr-0">
                      <InputAffix suffix="%" className={`peb-input-affix--sm max-w-80 justify-self-end ${watchSub[j].agreed ? '' : 'invisible'}`}>
                        <Input type="text" className="text-right" {...register(`sub.${j}.weight`)} readOnly={isFixedWeigt(sub, true) || readOnlyMode} maxLength={6} />
                      </InputAffix>
                    </td>
                  }
                </tr>)
              }
            </tbody>
          </table>

          <div>
            <span>{editableTranslate('balanced_scorecard.details_milestones')}</span>
            <EditorInput data-testid="details-input"
              className={`mt-2 ${readOnlyMode ? 'hide-toolbar' : ''}`}
              value={watchDetails}
              onChangeCallback={(v) => setValue('details', v, { shouldDirty: true, shouldValidate: true })}
              validate={(v) => v ? setDetailsError(true) : setDetailsError(false)}
              readOnly={readOnlyMode} />
          </div>
        </div>
      </form>
    }
  </div>
}

export default function AgreementMode({ settings, balancedScorecardDetails, fetchBalancedScorecardDetails, saveAgreement, evaluationCompleted, setError, assignedTeamObjectives, individualObjectives, reviewerView = false, setAgreementDirty, weightingErrorTranslationKey = null, isModal = false }) {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const [showAddIndividualObjective, setShowAddIndividualObjective] = useState(false);

  const [activeObjective, setActiveObjective] = useState(null);
  const [activeObjectiveDirty, setActiveObjectiveDirty] = useState(false);

  const [displayTeamObjectiveSelect, setDisplayTeamObjectiveSelect] = useState(false);

  const [teamObjectives, setTeamObjectives] = useState([]);

  const [activeSystematicObjective, setActiveSystematicObjective] = useState(null);
  const [activeSystematicObjectiveDirty, setActiveSystematicObjectiveDirty] = useState(false);

  const errorRef = useRef(null);

  const weighted = useMemo(() => {
    return settings?.performanceResultCalculation !== 'sum_of_all_unweighted';
  }, [settings]);

  const readOnlyMode = useMemo(() => {
    return evaluationCompleted || reviewerView;
  }, [evaluationCompleted, reviewerView]);

  const totalIndividual = useMemo(() => {
    let total = 0;
    if (individualObjectives && assignedTeamObjectives) {
      individualObjectives.forEach(o => total += parseFloat(formatNumberTo(o?.weight || 0, '.')));
      assignedTeamObjectives.forEach(o => total += parseFloat(formatNumberTo(o?.weight || 0, '.')));
    }
    return total;
  }, [individualObjectives, assignedTeamObjectives]);

  const [readOnlyWeight, setReadOnlyWeight] = useState(true);

  const schema = yup.object({
    weight: yup.object().shape({
      individual: yup.string()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-100', value => {
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 100;
        }).required(),
      systematic: yup.string()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-100', value => {
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 100;
        }).required()
    })
  });

  const { register, reset, getValues, trigger, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (displayTeamObjectiveSelect && settings?.howToAppraisal.includes('individual')) fetchTeamObjectives();
  }, [displayTeamObjectiveSelect]);

  useEffect(() => {
    reset({
      weight: balancedScorecardDetails?.agreement_data?.weight || {}
    });
  }, [balancedScorecardDetails]);

  useEffect(() => {
    setActiveObjectiveDirty(false);
  }, [activeObjective, showAddIndividualObjective]);

  useEffect(() => {
    setActiveSystematicObjectiveDirty(false);
  }, [activeSystematicObjective]);

  useEffect(() => {
    const check = ((!!activeObjective || showAddIndividualObjective) && activeObjectiveDirty) || (activeSystematicObjective !== null && activeSystematicObjectiveDirty) || (!readOnlyWeight && isDirty);
    setAgreementDirty(check);
  }, [activeObjective, activeObjectiveDirty, activeSystematicObjective, activeSystematicObjectiveDirty, showAddIndividualObjective, readOnlyWeight, isDirty]);

  useEffect(() => () => setAgreementDirty(false), []);

  const closeTeamObjectiveSelect = () => {
    setDisplayTeamObjectiveSelect(false);
    setActiveObjective(null);
  };

  const fetchTeamObjectives = async () => {
    const settingHashId = balancedScorecardDetails?.balanced_scorecard_setting?.hash_id;
    if (!settingHashId) return;

    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/team-objectives?setting_hash_id=${settingHashId}`);
      if (status === 200) {
        const temp = data.data.filter(d => {
          return assignedTeamObjectives?.filter(o => o.hash_id === d.hash_id).length > 0 ? false : true
        });
        setTeamObjectives(temp);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleEditWeight = async () => {
    if (readOnlyWeight) {
      setReadOnlyWeight(false);
    } else if (balancedScorecardDetails && await trigger()) {
      let agreementData = balancedScorecardDetails?.agreement_data;
      if (!agreementData) agreementData = {};

      agreementData.weight = getValues('weight');

      let total = 0;
      for (const k in agreementData.weight) {
        total += parseFloat(formatNumberTo(agreementData.weight[k], '.'));
      }

      if (total < 0 || total > 100) {
        setError({ translationKey: 'evaluation.validation.total_weight' });
        return;
      }

      setReadOnlyWeight(true);

      await saveAgreement(agreementData);
    }
  };

  const handleClickWeightingError = (e) => {
    if (['i', 'button'].includes(e?.target?.tagName?.toLowerCase()) ||
      e?.target?.getAttribute('role') === 'button') return;

    if (weightingErrorTranslationKey === 'evaluation.validation.total_weight') {
      setReadOnlyWeight(false);
    }

    const invalidDiv = document.querySelector('.is-invalid-border');
    if (!invalidDiv) return;

    let offset = 50;
    if (errorRef?.current) {
      offset += errorRef?.current?.offsetHeight;
    }

    if (isModal) {
      const modalBody = document.querySelector('.preview-balance-scorecard-modal .modal-body');

      if (modalBody) {
        const top = invalidDiv.getBoundingClientRect().top + (modalBody.scrollTop - 50) - offset;
        modalBody.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      let y = invalidDiv.getBoundingClientRect().top + (window.scrollY - (navHeight() ?? 0)) - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div>
      {
        weightingErrorTranslationKey && <div data-testid="weighting-error" ref={errorRef} onClick={handleClickWeightingError} className={`sticky z-2 pt-3 bg-white cursor-pointer mt-4`}>
          <ErrorAlert error={{ translationKey: weightingErrorTranslationKey }} type="warning" />
        </div>
      }

      {
        settings?.howToAppraisal.includes('individual') && <>
          <div className="mt-4">
            <div className={`d-flex ${readOnlyMode ? 'justify-content-end' : 'justify-content-between'} align-items-center`}>
              {
                !readOnlyMode && <div className="agreement-buttons-div">
                  <Button variant="secondary" onClick={() => setShowAddIndividualObjective(!showAddIndividualObjective)} data-testid="add-individual-objective-button"><SingleGoalAdd /> {editableTranslate('balanced_scorecard.add_individual_objective')}</Button>
                  <Button variant="secondary" onClick={() => setDisplayTeamObjectiveSelect(!displayTeamObjectiveSelect)} data-testid="assign-team-objective-button"><TeamGoalAdd /> {editableTranslate('balanced_scorecard.assign_team_objective')}</Button><i className={`bi bi-info-circle v-middle`} id="info-team-objective-overview"></i>

                  <ReactTooltip anchorSelect={`#info-team-objective-overview`} place="right-start" clickable>
                    {editableTranslate('evaluation.manage_team_objective')} <Link data-testid="team-objectives-overview-link" to="/evaluation/team-objectives-overview" target="_blank">{editableTranslate('global.link', 'link')}</Link>.
                  </ReactTooltip>
                </div>
              }
              {
                (settings?.howToAppraisal === 'individual_and_systematic_objective' && weighted) && <div className="text-right d-flex align-items-center">
                  {
                    !readOnlyMode && <Button variant="ghost" size="inline" className="mr-2" icon={readOnlyWeight ? 'bi-pencil-square' : 'bi-floppy'} onClick={handleEditWeight} data-testid="edit-weight-button">
                      {editableTranslate(readOnlyWeight ? 'edit' : 'button.save')}
                    </Button>
                  }
                  <div className={`combination-weight-div ${weightingErrorTranslationKey === 'evaluation.validation.total_weight' ? 'is-invalid-border' : ''}`}>
                    <img src={personIcon} alt="Person icon" /> / <img src={peopleIcon} alt="People icon" />
                    <div className="d-inline-block ml-2">
                      <InputAffix suffix="%" className="peb-input-affix--sm" error={!!errors?.weight?.individual} readOnly={readOnlyWeight}>
                        <Input type="text" className="text-right" error={!!errors?.weight?.individual} {...register('weight.individual')} readOnly={readOnlyWeight} maxLength={6} data-testid="weight-individual-input" />
                      </InputAffix>
                    </div>
                    <span className="vertical-separator mx-3">|</span>
                    <img src={toolIcon} alt="Tool icon" />
                    <div className="d-inline-block ml-2">
                      <InputAffix suffix="%" className="peb-input-affix--sm" error={!!errors?.weight?.systematic} readOnly={readOnlyWeight}>
                        <Input type="text" className="text-right" error={!!errors?.weight?.systematic} {...register('weight.systematic')} readOnly={readOnlyWeight} maxLength={6} data-testid="weight-systematic-input" />
                      </InputAffix>
                    </div>
                  </div>
                </div>
              }
            </div>

            {
              displayTeamObjectiveSelect && <div className="mt-3">
                <p className="mb-2">{editableTranslate('evaluation.select_team_objective')}</p>
                {
                  teamObjectives.map((obj, i) => <TeamObjective key={i} data={obj} settings={settings} setError={setError} activeObjective={activeObjective} setActiveObjective={setActiveObjective} setActiveSystematicObjective={setActiveSystematicObjective} balancedScorecardDetails={balancedScorecardDetails} fetchBalancedScorecardDetails={fetchBalancedScorecardDetails} setDisplayTeamObjectiveSelect={setDisplayTeamObjectiveSelect} readOnlyMode={readOnlyMode} totalIndividual={0} setActiveObjectiveDirty={setActiveObjectiveDirty} weighted={weighted} />)
                }
                {
                  teamObjectives.length < 1 && <span>{editableTranslate('evaluation.no_team_objective')}</span>
                }
                <div className="mt-3">
                  <Button variant="secondary" onClick={closeTeamObjectiveSelect} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                </div>
                <ReactTooltip anchorSelect={`#info-select-team-objective-description`} place="right-start" clickable>
                  {editableTranslate('evaluation.select_team_objective_description')}
                </ReactTooltip>
                <hr />
              </div>
            }
          </div>
          <div className={`mt-3 ${weightingErrorTranslationKey?.includes('individual') ? 'is-invalid-border pt-3' : ''}`}>
            <div>
              {showAddIndividualObjective && <IndividualObjective data={{}} mode="add" setDisplay={setShowAddIndividualObjective} activeObjective={activeObjective} setActiveObjective={setActiveObjective} setActiveSystematicObjective={setActiveSystematicObjective} settings={settings} setError={setError} balancedScorecardDetails={balancedScorecardDetails} fetchBalancedScorecardDetails={fetchBalancedScorecardDetails} readOnlyMode={readOnlyMode} totalIndividual={0} setActiveObjectiveDirty={setActiveObjectiveDirty} weighted={weighted} />}

              {
                assignedTeamObjectives && assignedTeamObjectives.map((obj, i) => <TeamObjective key={i} data={obj} mode="edit" settings={settings} setError={setError} activeObjective={activeObjective} setActiveObjective={setActiveObjective} setActiveSystematicObjective={setActiveSystematicObjective} balancedScorecardDetails={balancedScorecardDetails} fetchBalancedScorecardDetails={fetchBalancedScorecardDetails} setDisplayTeamObjectiveSelect={setDisplayTeamObjectiveSelect} readOnlyMode={readOnlyMode} totalIndividual={totalIndividual} setActiveObjectiveDirty={setActiveObjectiveDirty} weighted={weighted} />)
              }

              {
                individualObjectives && individualObjectives.map((obj, i) => <IndividualObjective key={i} data={obj} mode="edit" activeObjective={activeObjective} setActiveObjective={setActiveObjective} setActiveSystematicObjective={setActiveSystematicObjective} settings={settings} setError={setError} balancedScorecardDetails={balancedScorecardDetails} fetchBalancedScorecardDetails={fetchBalancedScorecardDetails} readOnlyMode={readOnlyMode} totalIndividual={totalIndividual} setActiveObjectiveDirty={setActiveObjectiveDirty} weighted={weighted} />)
              }

              {
                (balancedScorecardDetails && (!assignedTeamObjectives || !individualObjectives)) && <div className="my-2" data-testid="loading">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="sr-only">Wird geladen...</span>
                  </div>
                </div>
              }

              {
                (!balancedScorecardDetails || (assignedTeamObjectives?.length === 0 && individualObjectives?.length === 0 && !showAddIndividualObjective && !displayTeamObjectiveSelect)) && <p>{editableTranslate('evaluation.no_individual_objectives_in_lk')}</p>
              }
            </div>
          </div>
        </>
      }

      {
        settings?.howToAppraisal.includes('systematic') && <hr />
      }

      {
        settings?.howToAppraisal.includes('systematic') && <div className={`mt-4 ${['evaluation.validation.total_weight_criteria', 'evaluation.validation.total_weight_systematic'].includes(weightingErrorTranslationKey) ? 'is-invalid-border pt-2' : ''}`}>
          {
            settings?.shouldHasSubCriteria === false && settings?.systematicObjectiveDatagrid && settings?.systematicObjectiveDatagrid.map((item, i) =>
              <SystematicObjective key={i} index={i} data={item} settings={settings} balancedScorecardDetails={balancedScorecardDetails} activeSystematicObjective={activeSystematicObjective} setActiveSystematicObjective={setActiveSystematicObjective} setActiveObjective={setActiveObjective} saveAgreement={saveAgreement} readOnlyMode={readOnlyMode} setActiveSystematicObjectiveDirty={setActiveSystematicObjectiveDirty} setError={setError} weighted={weighted} />)
          }
          {
            settings?.shouldHasSubCriteria === true && settings?.systematicObjectiveDatagrid_subCriteria && settings?.systematicObjectiveDatagrid_subCriteria.map((item, i) =>
              <SystematicObjective key={i} index={i} data={item} settings={settings} balancedScorecardDetails={balancedScorecardDetails} hasSub={true} activeSystematicObjective={activeSystematicObjective} setActiveSystematicObjective={setActiveSystematicObjective} setActiveObjective={setActiveObjective} saveAgreement={saveAgreement} readOnlyMode={readOnlyMode} setActiveSystematicObjectiveDirty={setActiveSystematicObjectiveDirty} setError={setError} weighted={weighted} />)
          }
        </div>
      }
    </div>
  );
}
